import type {
  ModeratedReview,
  ReviewHiddenReason,
  ReviewModerationFilter,
} from './review-moderation.api';

export type ReviewModerationTab = 'reports' | 'all' | 'hidden';

export interface ReviewModerationFilters {
  stars: number | null;
  profile: { uuid: string; label: string } | null;
}

export type ReviewModerationSource =
  | { kind: 'openReports' }
  | { kind: 'reviews'; filter: ReviewModerationFilter };

// The reports tab is the open Review report queue, which the API does not filter.
export function resolveModerationSource(
  tab: ReviewModerationTab,
  filters: ReviewModerationFilters
): ReviewModerationSource {
  if (tab === 'reports') {
    return { kind: 'openReports' };
  }

  const filter: ReviewModerationFilter = {};
  if (filters.stars !== null) {
    filter.stars = filters.stars;
  }
  if (filters.profile) {
    filter.profileUuid = filters.profile.uuid;
  }
  if (tab === 'hidden') {
    filter.hidden = true;
  }

  return { kind: 'reviews', filter };
}

export function canHideReview(review: ModeratedReview): boolean {
  return !review.hiddenAt;
}

export function canRejectReport(review: ModeratedReview): boolean {
  return review.report?.state === 'OPEN';
}

// Whether a decided Review still belongs on the tab it was decided on.
export function keepsReviewOnTab(tab: ReviewModerationTab, review: ModeratedReview): boolean {
  if (tab === 'reports') {
    return canRejectReport(review);
  }
  if (tab === 'hidden') {
    return Boolean(review.hiddenAt);
  }

  return true;
}

export function getReviewProfileLabel(review: ModeratedReview, fallback: string): string {
  const profile = review.profile;
  if (!profile) {
    return fallback;
  }

  const name = [profile.firstName, profile.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const title = profile.title?.trim() || '';

  if (name && title) {
    return `${name} · ${title}`;
  }

  return name || title || fallback;
}

export function getHiddenReasonKey(
  reason: ReviewHiddenReason
): 'reasonSpam' | 'reasonAbuse' | 'reasonFake' | 'reasonNotAboutProvider' {
  switch (reason) {
    case 'SPAM':
      return 'reasonSpam';
    case 'ABUSE':
      return 'reasonAbuse';
    case 'FAKE':
      return 'reasonFake';
    default:
      return 'reasonNotAboutProvider';
  }
}

export function formatModerationDate(value: string | null | undefined, locale: string): string {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(
    new Date(timestamp)
  );
}
